import React, { useCallback, useEffect, useState } from 'react';
import cookie from 'react-cookies';
import getServices from '../../Services/get-service';
import Checkbox from '@mui/material/Checkbox';
import more from '../../Assets/images/more.svg';
import { Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';

function NewUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokenReady, setTokenReady] = useState(() => Boolean(cookie.load('token')));
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);

  const usersPerPage = 10;

  const fetchLatestUsers = useCallback(async (requestState = { cancelled: false }, retryCount = 0) => {
    setLoading(true);
    let willRetry = false;

    try {
      const response = await getServices.getAllUserHalal(null, null, 0);
      if (requestState.cancelled) return;

      const data = response.data?.data || {};
      const latestUsers = (data.users || [])
        .filter((user) => user.emailAddress)
        .slice(0, 10);

      setUsers(latestUsers);
    } catch (error) {
      if (requestState.cancelled) return;

      const status = error.response?.status;
      const shouldRetry = retryCount < 2 && (!status || status === 401 || status === 403);

      if (shouldRetry) {
        willRetry = true;
        setTimeout(() => fetchLatestUsers(requestState, retryCount + 1), 1000);
        return;
      }

      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      if (!requestState.cancelled && !willRetry) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (tokenReady) return;

    const checkToken = () => {
      if (cookie.load('token')) {
        setTokenReady(true);
      }
    };

    checkToken();
    const tokenCheckInterval = setInterval(checkToken, 250);

    return () => {
      clearInterval(tokenCheckInterval);
    };
  }, [tokenReady]);

  useEffect(() => {
    if (!tokenReady) return;

    const requestState = { cancelled: false };

    fetchLatestUsers(requestState);

    return () => {
      requestState.cancelled = true;
    };
  }, [tokenReady, fetchLatestUsers]);

  const handleOpenMenu = (event, userId) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  const viewUserProfile = (userId) => {
    handleCloseMenu();

    const user = users?.find((u) => u.id === userId);

    if (user) {
      setSelectedUserProfile(user);
      setShowProfileModal(true);
    } else {
      alert("Failed to load user profile");
    }
  };

  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
  };

  return (
    <div className="w-full px-2 sm:px-4">
      <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-0">
        <p className="text-text text-xl">New Users</p>
        <Link
          to="/customers"
          className="text-primary-color font-medium hover:underline"
        >
          See All
        </Link>
      </div>

      <div className="hidden md:flex flex-row justify-between items-center bg-primary-bg rounded-t-md rounded-r-md border-primary-border border p-4 mt-4">
        <Checkbox />
        <p className="text-text flex-1 text-center">Name</p>
        <p className="text-text flex-1 text-center">Phone</p>
        <p className="text-text flex-1 text-center">Account Number</p>
        <p className="text-text flex-1 text-center">Date Added</p>
        <p className="text-text flex-1 text-center">Status</p>
        <p className="text-text flex-1 text-center">Action</p>
      </div>

      {loading ? (
        <div>
          {[...Array(usersPerPage)].map((_, index) => (
            <div
              key={index}
              className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white border-b border-gray-200 p-4 gap-2"
            >
              <div className="w-6 md:w-[5%]">
                <Skeleton width={24} height={24} />
              </div>

              <div className="flex-1 text-left md:text-center">
                <Skeleton width="80%" height={20} className="mb-2" />
                <Skeleton width="60%" height={16} />
              </div>

              <div className="flex-1 text-left md:text-center">
                <Skeleton width="60%" height={20} />
              </div>

              <div className="flex-1 text-left md:text-center">
                <Skeleton width="60%" height={20} />
              </div>

              <div className="flex-1 text-left md:text-center">
                <Skeleton width="60%" height={20} />
              </div>

              <div className="flex-1 text-left md:text-center">
                <Skeleton width="60%" height={20} />
              </div>

              <div className="flex-1 text-left md:text-center">
                <Skeleton width={24} height={24} className="mx-auto" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="md:hidden mt-4">
            {users?.map((user, index) => (
              <div
                key={index}
                className="border rounded-lg p-4 mb-3 bg-white shadow-sm"
              >
                <p className="font-semibold">
                  {user.firstName || "N/A"} {user.lastName || "N/A"}
                </p>

                <p className="text-sm text-gray-500">
                  {user.emailAddress || "N/A"}
                </p>

                <p>
                  <b>Phone:</b> {user.phoneNumber || "N/A"}
                </p>

                <p>
                  <b>Account Number:</b> {user.accountNumber || "N/A"}
                </p>

                <p>
                  <b>Date Added:</b>{" "}
                  {user.createdAt ? user.createdAt.split("T")[0] : "N/A"}
                </p>

                <p>
                  <b>Status:</b>{" "}
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-sm font-medium ${
                      user.accountStatus === "active"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {user.accountStatus === "active" ? "Active" : "Inactive"}
                  </span>
                </p>

                <div className="flex justify-end mt-2">
                  <img
                    src={more}
                    alt="More options"
                    className="cursor-pointer"
                    onClick={(e) => handleOpenMenu(e, user.id)}
                  />

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl && selectedUserId === user.id)}
                    onClose={handleCloseMenu}
                    sx={{
                      "& .MuiPaper-root": {
                        boxShadow: "none",
                      },
                    }}
                  >
                    <MenuItem onClick={() => viewUserProfile(user.id)}>
                      View user profile
                    </MenuItem>
                  </Menu>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden md:block">
            {users?.map((user, index) => (
              <div
                key={index}
                className="flex flex-row justify-between items-center bg-white border-b border-gray-200 p-4"
              >
                <Checkbox />

                <div className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                  <p>
                    {`${(user.firstName || "N/A").toUpperCase()} ${(
                      user.lastName || "N/A"
                    ).toUpperCase()}`}
                  </p>
                  <h4 className="text-sm text-gray-500">
                    {user.emailAddress || "N/A"}
                  </h4>
                </div>

                <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                  {user.phoneNumber || "N/A"}
                </p>

                <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                  {user.accountNumber || "N/A"}
                </p>

                <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                  {user.createdAt ? user.createdAt.split("T")[0] : "N/A"}
                </p>

                <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-sm font-medium ${
                      user.accountStatus === "active"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {user.accountStatus === "active" ? "Active" : "Inactive"}
                  </span>
                </p>

                <div className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                  <img
                    src={more}
                    alt="More options"
                    className="mx-auto cursor-pointer"
                    onClick={(e) => handleOpenMenu(e, user.id)}
                  />

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl && selectedUserId === user.id)}
                    onClose={handleCloseMenu}
                    sx={{
                      "& .MuiPaper-root": {
                        boxShadow: "none",
                      },
                    }}
                  >
                    <MenuItem onClick={() => viewUserProfile(user.id)}>
                      View user profile
                    </MenuItem>
                  </Menu>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog
        open={showProfileModal}
        onClose={handleCloseProfileModal}
        keepMounted={false}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>User Profile</DialogTitle>

        <DialogContent>
          {selectedUserProfile ? (
            <div className="space-y-3 pt-2">
              <div>
                <p className="text-sm text-gray-500">First Name</p>
                <p className="font-medium">
                  {selectedUserProfile.firstName || "N/A"}
                </p>
              </div>

              {selectedUserProfile.middleName && (
                <div>
                  <p className="text-sm text-gray-500">Middle Name</p>
                  <p className="font-medium">
                    {selectedUserProfile.middleName}
                  </p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500">Last Name</p>
                <p className="font-medium">
                  {selectedUserProfile.lastName || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Email Address</p>
                <p className="font-medium">
                  {selectedUserProfile.emailAddress || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Phone Number</p>
                <p className="font-medium">
                  {selectedUserProfile.phoneNumber || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Account Number</p>
                <p className="font-medium">
                  {selectedUserProfile.accountNumber || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Bank Name</p>
                <p className="font-medium">
                  {selectedUserProfile.bankName || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Account Status</p>
                <p
                  className={
                    selectedUserProfile.accountStatus === "active"
                      ? "font-medium text-green-600"
                      : "font-medium text-red-600"
                  }
                >
                  {selectedUserProfile.accountStatus === "active"
                    ? "Active"
                    : "Inactive"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">KYC Status</p>
                <p className="font-medium">
                  {selectedUserProfile.kycStatus ? "Verified" : "Not Verified"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Date Joined</p>
                <p className="font-medium">
                  {selectedUserProfile.createdAt
                    ? new Date(selectedUserProfile.createdAt).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )
                    : "N/A"}
                </p>
              </div>
            </div>
          ) : (
            <p>No profile data available</p>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseProfileModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default NewUsers;
